import { connect } from 'react-redux';
import { Box, Grid, IconButton, Typography } from "@mui/material";

import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { ReactComponent as ARMenuImg } from "../../assets/images/ar_view.svg";
import MenuListAddSubMenuBtn from "../MenuListAddSubMenuBtn";

import styles from "./MenuItemCardImageRight.module.scss";
import { formatPrice, MENU_TYPE2 } from "../../constants";
import { toggleFavorite, selectIsFavorite } from "../../redux/actions/userDataActions";

import NutritionBadge from "./NutritionBadge";
import MenuImage from "./MenuImage";

const MenuItemCardImageRight = ({
  image,
  name,
  description,
  price,
  currency,
  menuType,
  dishNotes = [],
  hideMenuThumbNail = false,
  onClick,
  bgColor,
  fontColor,
  dishNotesBgColor,
  dishNotesFontColor,
  imageBgColor,
  hasARModel,
  onArViewClick,
  arBtnBgColor,
  arBtnIconColor,
  addToCartExist,
  cartCount,
  addBtnBgColor,
  addBtnFontColor,
  paddingHorizontal = 16,
  isItemAvailable = true,
  availabilityMessage = "",
  allergenList,
  allergenIds,
  menuContainerRef,
  menuAttributesList,
  attributes,
  menuItemId,
  isFavorite,
  dispatch,
}) => {
  const menuWidth =
    !image && !addToCartExist && hideMenuThumbNail
      ? 0
      : hideMenuThumbNail && !addToCartExist
        ? 40
        : 120;
  const menuHeight =
    !image && hideMenuThumbNail ? 0 : hideMenuThumbNail ? 50 : 120;

  const ARMenuLogo = () => {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{
          backgroundColor: arBtnBgColor ?? "rgba(0, 0, 0, 0.7)",
          position: "absolute",
          top: 0,
          right: 0,
          px: "6px",
          py: "3px",
          borderBottomLeftRadius: 6,
          path: {
            fill: arBtnIconColor ?? "#B58C67",
          },
        }}
      >
        <ARMenuImg width={30} height={30} />
      </Box>
    );
  };

  const getAttribute = (attributeId) => {
    return (menuAttributesList ?? []).find(x => x._id === attributeId) ?? null;
  }

  return (
    <Grid
      className={styles.menuListItem}
      item
      xs={12}
      px={`${paddingHorizontal}px`}
      onClick={isItemAvailable ? onClick : undefined}
      sx={{
        position: "relative",
        opacity: isItemAvailable ? 1 : 0.6,
        cursor: isItemAvailable ? "pointer" : "not-allowed",
        flexDirection: "column"
      }}
    >
      {!isItemAvailable && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              color: "#fff",
              padding: "8px 16px",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            {availabilityMessage}
          </Box>
        </Box>
      )}

      {/* Row 1: Main card content */}
      <Box className={styles["menu-card-main-row"]}>
        <Box className={styles["menu-item-view"]}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: "8px", alignItems: "flex-start" }}>
            <Typography
              className={styles["menu-name-text"]}
              sx={{
                color: fontColor ?? "#1d3153",
              }}
            >
              {name}
            </Typography>

            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <Box sx={{
                width: '20px',
                height: '20px',
                padding: '4px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                {MENU_TYPE2[menuType]}
              </Box>

              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(toggleFavorite(menuItemId));
                }}
                sx={{ p: 0.5 }}
              >
                {isFavorite ? (
                  <FavoriteIcon sx={{ fontSize: 20, color: "#e53935" }} />
                ) : (
                  <FavoriteBorderIcon sx={{ fontSize: 20, color: fontColor ?? "#1d3153" }} />
                )}
              </IconButton>
            </Box>
          </Box>

          {dishNotes.length ? (
            <Box className={`${styles["dish-notes-main-view"]}`}>
              {dishNotes.map((x, i) => {
                return (
                  <Box
                    key={i}
                    className={styles.dishNoteView}
                    sx={{
                      backgroundColor: dishNotesBgColor ?? "#ef3c34",
                    }}
                  >
                    <Typography
                      sx={{
                        color: dishNotesFontColor ?? "#fff",
                      }}
                      className={styles.dishNoteText}
                    >
                      {x}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : null}

          <Typography
            className={`${styles["menu-price-text"]}`}
            sx={{ color: fontColor ?? "#1d3153", py: 1 }}
          >
            {formatPrice(price, currency)}
          </Typography>

          <Typography
            className={`${styles["menu-desc-text"]}`}
            sx={{
              color: fontColor ?? "#011c3a",
              pb: 1.5,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxHeight: "3.75rem",
              WebkitLineClamp: 3,
              lineHeight: "1.4rem",
            }}
          >
            {description}
          </Typography>
        </Box>

        <Box className={styles.menuRightView} pl={1}>
          {image || hideMenuThumbNail ? (
            <Box
              className={`${styles["menu-img-view"]} cursor-pointer`}
              style={{
                height: menuHeight,
                width: menuWidth,
              }}
              onClick={(e) => {
                if (hasARModel) {
                  e.stopPropagation();
                  onArViewClick();
                }
              }}
            >
              {!hideMenuThumbNail ? (
                <MenuImage
                  key={image}
                  className={styles.menuImgStyle}
                  src={image}
                  alt={name}
                />
              ) : null}
              {hasARModel ? <ARMenuLogo /> : null}
            </Box>
          ) : (
            <></>
          )}

          {addToCartExist ? (
            cartCount ? (
              <Box
                className={`${styles?.["menu-add-sub-btn"]}`}
                sx={{
                  mt: hideMenuThumbNail ? 0 : "-15px",
                }}
              >
                <MenuListAddSubMenuBtn
                  theme={"theme1"}
                  count={cartCount}
                  onAddSubMenu={(e) => {
                    e.stopPropagation();
                    onClick();
                  }}
                />
              </Box>
            ) : (
              <Box
                className={styles.menuAddView}
                sx={{
                  backgroundColor: addBtnBgColor ?? "#b99c74",
                  mt: hideMenuThumbNail ? 0 : "-15px",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onClick();
                }}
              >
                <Typography
                  className={styles.menuAddText}
                  sx={{
                    color: addBtnFontColor ?? "#FFF",
                  }}
                >
                  ADD +
                </Typography>
              </Box>
            )
          ) : null}
        </Box>
      </Box>

      {/* Row 2: Menu Attributes icons */}
      {attributes?.length > 0 && (
        <Box className={styles["allergen-row"]} style={{ paddingTop: "16px" }}>
          {attributes.map((item, index) => {
            const attribute = getAttribute(item._idAttribute);
            if (!attribute) return null;
            return <NutritionBadge attribute={attribute} value={item.value} />
          })}
        </Box>
      )}
    </Grid >
  );
};

const mapStateToProps = (state, ownProps) => ({
  allergenList: state.apiData.allergenList,
  menuAttributesList: state.apiData.menuAttributesList,
  isFavorite: selectIsFavorite(state, ownProps.menuItemId),
});

export default connect(mapStateToProps)(MenuItemCardImageRight);
